import { useState, type KeyboardEvent } from 'react';
import styled from 'styled-components';
import {
  useDeleteAssistantSessionMutation,
  useRenameAssistantSessionMutation,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import { IconPencil, IconTrash } from '@shared/ui/icons';
import { AssistantChat } from '@widgets/Assistant/AssistantChat';
import { useActiveAssistantSession } from '@widgets/Assistant/useActiveAssistantSession';
import type { AssistantSession } from '@entities/assistant/types';

const Head = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(4)};

  h1 {
    margin: 0 0 ${({ theme }) => theme.spacing(1)};
    font-size: 22px;
  }
  p {
    margin: 0;
    color: ${({ theme }) => theme.colors.text.secondary};
    font-size: 14px;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 280px minmax(0, 1fr);
  gap: ${({ theme }) => theme.spacing(5)};
  height: calc(100vh - 220px);
  min-height: 480px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    height: auto;
  }
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
  min-height: 0;
  overflow-y: auto;
`;

const Row = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  border: 1px solid
    ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.border)};
  background: ${({ theme, $active }) =>
    $active ? theme.colors.primarySoft : theme.colors.backgroundElevated};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing(1)};
`;

const Title = styled.button`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
  border: 0;
  background: none;
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: 14px;
  padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(2)}`};
  cursor: pointer;

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

const IconBtn = styled.button`
  display: inline-flex;
  flex: none;
  padding: ${({ theme }) => theme.spacing(1)};
  border: 0;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: none;
  color: ${({ theme }) => theme.colors.text.muted};
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

const RenameInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(2)}`};
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: 14px;
`;

const Muted = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.muted};
  padding: ${({ theme }) => theme.spacing(2)};
`;

const ChatPanel = styled.div`
  min-height: 0;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  overflow: hidden;

  @media (max-width: 900px) {
    height: 70vh;
  }
`;

function SessionRow({
  session,
  active,
  onOpen,
  onDeleted,
}: {
  session: AssistantSession;
  active: boolean;
  onOpen: () => void;
  onDeleted: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(session.title);
  const [rename] = useRenameAssistantSessionMutation();
  const [remove] = useDeleteAssistantSessionMutation();

  async function save() {
    const t = title.trim();
    setEditing(false);
    if (!t || t === session.title) {
      setTitle(session.title);
      return;
    }
    await rename({ sessionId: session.id, title: t })
      .unwrap()
      .catch(() => setTitle(session.title));
  }

  function onKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') void save();
    if (e.key === 'Escape') {
      setTitle(session.title);
      setEditing(false);
    }
  }

  async function del() {
    if (!window.confirm(`Удалить чат «${session.title}»? Вернуть его будет нельзя.`)) return;
    await remove(session.id).unwrap().catch(() => undefined);
    onDeleted();
  }

  return (
    <Row $active={active}>
      {editing ? (
        <RenameInput
          autoFocus
          value={title}
          maxLength={120}
          aria-label="Название чата"
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={onKey}
          onBlur={() => void save()}
        />
      ) : (
        <Title type="button" onClick={onOpen} title={session.title}>
          {session.title}
        </Title>
      )}
      <IconBtn type="button" aria-label="Переименовать чат" title="Переименовать" onClick={() => setEditing(true)}>
        <IconPencil size={15} />
      </IconBtn>
      <IconBtn type="button" aria-label="Удалить чат" title="Удалить" onClick={() => void del()}>
        <IconTrash size={15} />
      </IconBtn>
    </Row>
  );
}

/** ОСНОВА.Помощник на весь экран: свои чаты слева, чат справа. */
export function AssistantPage() {
  const { active, setActive, sessions } = useActiveAssistantSession();
  const items = sessions.data?.items ?? [];

  return (
    <>
      <Head>
        <h1>Помощник</h1>
        <p>
          Отвечает на вопросы о работе в ОСНОВЕ и на рабочие вопросы. Ваши чаты видите только вы.
        </p>
      </Head>
      <Grid>
        <Sidebar>
          <Button type="button" $block onClick={() => setActive(null)}>
            + Новый чат
          </Button>
          {sessions.isLoading && <Muted>Загрузка…</Muted>}
          {!sessions.isLoading && items.length === 0 && (
            <Muted>Чатов пока нет — задайте первый вопрос.</Muted>
          )}
          {items.map((s) => (
            <SessionRow
              key={s.id}
              session={s}
              active={s.id === active}
              onOpen={() => setActive(s.id)}
              onDeleted={() => {
                if (s.id === active) setActive(null);
              }}
            />
          ))}
        </Sidebar>
        <ChatPanel>
          <AssistantChat sessionId={active} onSessionChange={setActive} page="/assistant" />
        </ChatPanel>
      </Grid>
    </>
  );
}
